import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import FormatAmount from './FormatAmount';
import FormatCryptoAmount from './FormatCryptoAmount';
import PageLayout from './PageLayout';

const AccountDetailsPage = ({ currentUser, companies, currentCompany, account, selectedSource, csrfToken }) => {
  const [entries, setEntries] = useState([]);
  const [unreconciledEntries, setUnreconciledEntries] = useState([]);
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUnreconciled, setIsLoadingUnreconciled] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [selectedAccountsByEntry, setSelectedAccountsByEntry] = useState({});
  const [reconciliationData, setReconciliationData] = useState(null);
  const [isLoadingReconciliation, setIsLoadingReconciliation] = useState(false);
  const [profitDataByEntry, setProfitDataByEntry] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [accountDetails, setAccountDetails] = useState(account);
  const [isLoadingAccountDetails, setIsLoadingAccountDetails] = useState(false);
  const [rules, setRules] = useState([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [rulesError, setRulesError] = useState(null);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showEditRuleModal, setShowEditRuleModal] = useState(false);
  const [showDeleteRuleDialog, setShowDeleteRuleDialog] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [newRule, setNewRule] = useState({
    description_pattern: '',
    target_account_id: null
  });
  const [editingRule, setEditingRule] = useState({
    id: null,
    description_pattern: '',
    target_account_id: null
  });
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [isUpdatingRule, setIsUpdatingRule] = useState(false);
  const [companyAccountsForRules, setCompanyAccountsForRules] = useState([]);
  const [isLoadingCompanyAccountsForRules, setIsLoadingCompanyAccountsForRules] = useState(false);
  const [showAutoReconcileDialog, setShowAutoReconcileDialog] = useState(false);
  const [isAutoReconciling, setIsAutoReconciling] = useState(false);

  // Get initial tab from URL or default to 'statements'
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    return ['reconcile', 'statements', 'rules'].includes(tab) ? tab : 'statements';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Helper function to get token name from account name
  const getTokenName = (accountName) => {
    const parts = accountName.split(' - ');
    return parts[parts.length - 1] || accountName;
  };

  // Helper function to check if account is cryptocurrency
  const isCryptoAccount = (accountName) => {
    const tokenName = getTokenName(accountName);
    const cryptoTokens = ['BTC', 'ETH', 'MATIC', 'SOL', 'USDC', 'USDT'];
    return cryptoTokens.includes(tokenName.toUpperCase());
  };

  // Helper function to get token icon
  const getTokenIcon = (tokenName) => {
    return `/tokens/${tokenName.toUpperCase()}.png`;
  };

  // Helper function to calculate profit percentage from reconciliation data
  const calculateProfitPercentage = (reconciliationData) => {
    if (!reconciliationData || !reconciliationData.amounts) return null;
    
    // Find profit entry (usually has 'profit' in account name)
    const profitEntry = reconciliationData.amounts.find(amount => 
      amount.account_name.toLowerCase().includes('profit')
    );
    
    // Find the main credit amount (what was spent)
    const creditAmounts = reconciliationData.amounts.filter(amount => amount.type === 'credit');
    const mainCreditAmount = creditAmounts.reduce((sum, amount) => sum + amount.amount, 0);
    
    if (profitEntry && mainCreditAmount > 0) {
      const profitAmount = profitEntry.amount;
      const profitPercentage = (profitAmount / mainCreditAmount) * 100;
      
      // In accounting, profit is recorded as CREDIT to revenue account
      // So if profit is in credit, it's positive; if in debit, it's negative
      const isPositive = profitEntry.type === 'credit';
      
      return {
        amount: profitAmount,
        percentage: profitPercentage,
        isPositive: isPositive
      };
    }
    
    return null;
  };

  // Helper function to generate profit explanation
  const generateProfitExplanation = (entry, reconciliationData, profitInfo) => {
    if (!profitInfo || entry.entry_type !== 'credit') return null;
    
    const transactionDate = formatDate(entry.date);
    const profitAmount = Number(profitInfo.amount) || 0;
    const profitPercentage = Number(profitInfo.percentage) || 0;
    const isPositive = profitInfo.isPositive;
    
    // Ensure profit amount is positive for display purposes
    const displayProfitAmount = Math.abs(profitAmount);
    const currency = currentCompany?.currency || 'GBP';
    
    // Get token amounts from entry metadata
    const spentMeta = entry.metadata?.spent || {};
    const receivedMeta = entry.metadata?.received || {};
    
    // Calculate token amounts
    const tokenAmount = Number(spentMeta.amount) || Number(receivedMeta.amount) || 0;
    const tokenAmountAbs = Math.abs(tokenAmount);
    
    // Get the main credit amount (what was spent in fiat)
    const creditAmounts = reconciliationData.amounts.filter(amount => amount.type === 'credit');
    const mainCreditAmount = Number(creditAmounts.reduce((sum, amount) => sum + (Number(amount.amount) || 0), 0)) || 0;
    
    // Get the main debit amount (what was received in fiat)
    const debitAmounts = reconciliationData.amounts.filter(amount => amount.type === 'debit' && !amount.account_name.toLowerCase().includes('profit'));
    const mainDebitAmount = Number(debitAmounts.reduce((sum, amount) => sum + (Number(amount.amount) || 0), 0)) || 0;
    
    // Calculate token prices
    const originalTokenPrice = tokenAmountAbs > 0 ? mainDebitAmount / tokenAmountAbs : 0;
    const currentTokenPrice = tokenAmountAbs > 0 ? mainCreditAmount / tokenAmountAbs : 0;
    
    // Get token name from account or description
    const tokenName = entry.description.match(/\b(ETH|BTC|USDC|USDT|GBP|USD|SOL|MATIC)\b/i)?.[0] || 'token';
    
    const explanation = {
      title: isPositive ? 'Profit Explanation' : 'Loss Explanation',
      content: `This transaction shows a ${profitPercentage.toFixed(2)}% ${isPositive ? 'gain' : 'loss'} because the ${tokenName} was purchased at a different price and later ${entry.description.toLowerCase().includes('withdraw') ? 'withdrawn' : 'sold'}. The original ${tokenName} price was ${originalTokenPrice.toFixed(2)} ${currency} per ${tokenName}, but the current ${tokenName} price is ${currentTokenPrice.toFixed(2)} ${currency} per ${tokenName}, resulting in a ${isPositive ? 'profit' : 'loss'} of ${displayProfitAmount.toFixed(2)} ${currency}.`,
      color: isPositive ? 'success.main' : 'error.main',
      bgColor: isPositive ? 'success.light' : 'error.light'
    };
    
    return explanation;
  };

  // Helper function to load profit data for all reconciled spent transactions
  const loadProfitDataForEntries = async (entries) => {
    const reconciledSpentEntries = entries.filter(entry => 
      entry.status === 'reconciled' && entry.entry_type === 'credit'
    );
    
    const profitData = {};
    
    for (const entry of reconciledSpentEntries) {
      try {
        const response = await fetch(`/api/internal/accounts/${account.id}/entries/${entry.id}/reconciliation`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });
        
        if (response.ok) {
          const reconciliationData = await response.json();
          const profitInfo = calculateProfitPercentage(reconciliationData);
          if (profitInfo) {
            profitData[entry.id] = profitInfo;
          }
        }
      } catch (error) {
        console.error(`Failed to load profit data for entry ${entry.id}:`, error);
      }
    }
    
    setProfitDataByEntry(profitData);
  };

  // Fetch account entries
  const fetchEntries = useCallback(async () => {
    if (!account?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/entries`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
    
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setEntries(data);
      
      // Load profit data for all reconciled spent transactions
      await loadProfitDataForEntries(data);
    } catch (err) {
      console.error('Failed to fetch account entries:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [account?.id, csrfToken]);

  // Fetch unreconciled entries using existing endpoint with status param
  const fetchUnreconciledEntries = useCallback(async () => {
    if (!account?.id) return;
    
    setIsLoadingUnreconciled(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/entries?status=unreconciled`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
    
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setUnreconciledEntries(data);
    } catch (err) {
      console.error('Failed to fetch unreconciled entries:', err);
      setError(err.message);
    } finally {
      setIsLoadingUnreconciled(false);
    }
  }, [account?.id, csrfToken]);

  // Fetch company accounts
  const fetchCompanyAccounts = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    setIsLoadingAccounts(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/internal/companies/${currentCompany.id}/accounts`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
    
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setCompanyAccounts(data);
    } catch (err) {
      console.error('Failed to fetch company accounts:', err);
      setError(err.message);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [currentCompany?.id, csrfToken]);

  // Fetch account details with balances
  const fetchAccountDetails = useCallback(async () => {
    if (!account?.id) return;
    
    setIsLoadingAccountDetails(true);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/details`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
    
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setAccountDetails(data);
    } catch (err) {
      console.error('Failed to fetch account details:', err);
    } finally {
      setIsLoadingAccountDetails(false);
    }
  }, [account?.id, csrfToken]);

  // Fetch account rules
  const fetchRules = useCallback(async () => {
    if (!account?.id) return;
    
    setIsLoadingRules(true);
    setRulesError(null);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/rules`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
    
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setRules(data);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
      setRulesError(err.message);
    } finally {
      setIsLoadingRules(false);
    }
  }, [account?.id, csrfToken]);

  // Create a new rule
  const handleCreateRule = async () => {
    if (!newRule.description_pattern.trim() || !newRule.target_account_id) {
      return;
    }

    setIsCreatingRule(true);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          description_pattern: newRule.description_pattern,
          target_account_id: newRule.target_account_id
          // priority will be auto-calculated by the backend
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create rule');
      }

      // Reset form and close modal
      setNewRule({
        description_pattern: '',
        target_account_id: null
      });
      setShowAddRuleModal(false);
      
      // Refresh rules list
      await fetchRules();
      
      // Apply auto-selection to existing unreconciled entries
      if (unreconciledEntries.length > 0) {
        unreconciledEntries.forEach(entry => {
          autoSelectAccountForEntry(entry);
        });
      }
    } catch (error) {
      console.error('Error creating rule:', error);
      alert(error.message);
    } finally {
      setIsCreatingRule(false);
    }
  };

  // Fetch company accounts for rules
  const fetchCompanyAccountsForRules = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    setIsLoadingCompanyAccountsForRules(true);
    
    try {
      const response = await fetch(`/api/internal/companies/${currentCompany.id}/accounts`);
      if (!response.ok) {
        throw new Error('Failed to fetch company accounts');
      }
      const data = await response.json();
      setCompanyAccountsForRules(data);
    } catch (error) {
      console.error('Error fetching company accounts for rules:', error);
    } finally {
      setIsLoadingCompanyAccountsForRules(false);
    }
  }, [currentCompany?.id]);

  // Edit a rule
  const handleEditRule = (rule) => {
    setEditingRule({
      id: rule.id,
      description_pattern: rule.description_pattern,
      target_account_id: rule.target_account_id
    });
    setShowEditRuleModal(true);
  };

  // Update a rule
  const handleUpdateRule = async () => {
    if (!editingRule.description_pattern.trim() || !editingRule.target_account_id) {
      return;
    }

    setIsUpdatingRule(true);
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/rules/${editingRule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          description_pattern: editingRule.description_pattern,
          target_account_id: editingRule.target_account_id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update rule');
      }

      // Reset form and close modal
      setEditingRule({
        id: null,
        description_pattern: '',
        target_account_id: null
      });
      setShowEditRuleModal(false);
      
      // Refresh rules list
      await fetchRules();
      
      // Re-apply auto-selection to existing unreconciled entries
      if (unreconciledEntries.length > 0) {
        unreconciledEntries.forEach(entry => {
          autoSelectAccountForEntry(entry);
        });
      }
    } catch (error) {
      console.error('Error updating rule:', error);
      alert(error.message);
    } finally {
      setIsUpdatingRule(false);
    }
  };

  // Delete a rule
  const handleDeleteRule = async (ruleId) => {
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete rule');
      }

      // Close dialog
      setShowDeleteRuleDialog(false);
      setRuleToDelete(null);

      // Refresh rules list
      await fetchRules();
      
      // Re-apply auto-selection to existing unreconciled entries
      if (unreconciledEntries.length > 0) {
        unreconciledEntries.forEach(entry => {
          autoSelectAccountForEntry(entry);
        });
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert(error.message);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    if (activeTab === 'reconcile') {
      fetchUnreconciledEntries();
      fetchCompanyAccounts();
      fetchRules(); // Cargar reglas también en el tab de reconciliación
    } else if (activeTab === 'statements') {
      fetchEntries();
    } else if (activeTab === 'rules') {
      fetchRules();
      fetchCompanyAccountsForRules();
    }
  }, [activeTab, fetchUnreconciledEntries, fetchCompanyAccounts, fetchEntries, fetchRules, fetchCompanyAccountsForRules]);

  // Initial fetch and polling for account details
  useEffect(() => {
    fetchAccountDetails();
    
    // Set up polling every 2 seconds
    const interval = setInterval(() => {
      fetchAccountDetails();
    }, 2000);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [fetchAccountDetails]);

  // Auto-select accounts for existing entries when rules are loaded
  useEffect(() => {
    if (rules.length > 0 && unreconciledEntries.length > 0) {
      unreconciledEntries.forEach(entry => {
        autoSelectAccountForEntry(entry);
      });
    }
  }, [rules, unreconciledEntries]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get('tab');
      const newTab = tab === 'reconcile' ? 'reconcile' : 'statements';
      setActiveTab(newTab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Calculate account balances
  const calculateBalances = () => {
    // Statement balance is the account's own balance (fiat)
    const statementBalance = accountDetails?.balance || 0;
    
    // Token balance is calculated from metadata (tokens)
    const tokenBalance = accountDetails?.token_balance || 0;
    
    // ContaCrypto balance is the dependency balance (if exists)
    const contacryptoBalance = accountDetails?.dependency?.balance || 0;
    
    // ContaCrypto token balance is the dependency token balance (if exists)
    const contacryptoTokenBalance = accountDetails?.dependency?.token_balance || 0;
    
    return {
      statementBalance,
      tokenBalance,
      contacryptoBalance,
      contacryptoTokenBalance
    };
  };

  const balances = calculateBalances();
  const tokenName = getTokenName(account?.name || '');
  const isCrypto = isCryptoAccount(account?.name || '');

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).toUpperCase();
  };

  // Get status chip color
  const getStatusChipProps = (status) => {
    switch (status) {
      case 'reconciled':
        return { bgcolor: '#dcfce7', color: '#166534', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
      case 'unreconciled':
        return { bgcolor: '#fef3c7', color: '#92400e', icon: <SyncIcon sx={{ fontSize: 16 }} /> };
      default:
        return { bgcolor: '#e5e7eb', color: '#374151' };
    }
  };

  const handleEntryClick = async (entry) => {
    // If entry is not reconciled, do nothing
    if (!entry.reconciliation_entry_id) {
      return;
    }

    // If already expanded, collapse
    if (expandedEntry === entry.id) {
      setExpandedEntry(null);
      setReconciliationData(null);
      return;
    }

    // Fetch reconciliation data
    setIsLoadingReconciliation(true);
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/entries/${entry.id}/reconciliation`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setReconciliationData(data);
      setExpandedEntry(entry.id);
      
      // Calculate and store profit data
      const profitInfo = calculateProfitPercentage(data);
      if (profitInfo) {
        setProfitDataByEntry(prev => ({
          ...prev,
          [entry.id]: profitInfo
        }));
      }
    } catch (error) {
      console.error('Failed to fetch reconciliation data:', error);
      // Still expand to show error
      setExpandedEntry(entry.id);
    } finally {
      setIsLoadingReconciliation(false);
    }
  };

  const handleAccountSelect = (entryId, eventOrValue, reason) => {
    // eventOrValue can be MUI Autocomplete's 'value'
    const newValue = eventOrValue;
    const selectedId = newValue ? newValue.id : '';
    setSelectedAccountsByEntry(prev => ({ ...prev, [entryId]: selectedId }));
  };

  // Auto-select account based on rules when entry is loaded
  const autoSelectAccountForEntry = (entry) => {
    const autoSelectedId = getAutoSelectedAccount(entry);
    if (autoSelectedId && !selectedAccountsByEntry[entry.id]) {
      setSelectedAccountsByEntry(prev => ({ ...prev, [entry.id]: autoSelectedId }));
    }
  };

  // Auto-select account based on rules
  const getAutoSelectedAccount = (entry) => {
    if (!rules.length) return null;
    
    // Find matching rule
    const matchingRule = rules.find(rule => 
      entry.description.toLowerCase().includes(rule.description_pattern.toLowerCase())
    );
    
    return matchingRule ? matchingRule.target_account_id : null;
  };

  const handleReconcileOk = async (entry) => {
    const selectedAccountId = selectedAccountsByEntry[entry.id];
    if (!selectedAccountId) return;
    
    // Determine which side (spent/received) corresponds to this account
    const spentMeta = entry.metadata?.spent || {};
    const receivedMeta = entry.metadata?.received || {};
    
    const isSpentForThisAccount = entry.entry_type === 'credit';
    const isReceivedForThisAccount = entry.entry_type === 'debit';

    let debitAccountId, creditAccountId;
    
    if (isReceivedForThisAccount) {
      // This account was debited (received), so it's the debit_account_id
      // The selected account becomes the credit_account_id
      debitAccountId = account.id;
      creditAccountId = selectedAccountId;
    } else {
      // This account was credited (spent), so it's the credit_account_id
      // The selected account becomes the debit_account_id
      debitAccountId = selectedAccountId;
      creditAccountId = account.id;
    }
    
    try {
      const response = await fetch('/api/internal/reconcile', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          entry_id: entry.id,
          debit_account_id: debitAccountId,
          credit_account_id: creditAccountId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Clear the selection for this entry
      setSelectedAccountsByEntry(prev => {
        const newState = { ...prev };
        delete newState[entry.id];
        return newState;
      });

      // Refresh data to update reconciliation status
      fetchUnreconciledEntries();
      fetchEntries(); // Also refresh all entries for wallet statements tab
      fetchAccountDetails();
      
      console.log('Reconciliation created successfully for entry:', entry.id);
      
    } catch (error) {
      console.error('Failed to create reconciliation:', error);
      alert('Failed to create reconciliation. Please try again.');
    }
  };

  const handleDeleteReconciliation = async () => {
    if (!entryToDelete) return;
    
    try {
      const response = await fetch(`/api/internal/accounts/${account.id}/entries/${entryToDelete.id}/reconciliation`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Close dialog and refresh data
      setShowDeleteDialog(false);
      setEntryToDelete(null);
      setExpandedEntry(null);
      setReconciliationData(null);
      
      // Refresh entries to update reconciliation status
      fetchEntries();
      
    } catch (error) {
      console.error('Failed to delete reconciliation:', error);
      alert('Failed to delete reconciliation. Please try again.');
    }
  };

  // Batch auto-reconcile all entries that have a selected account
  const handleAutoReconcileAll = async () => {
    if (!unreconciledEntries.length) return;
    setIsAutoReconciling(true);
    try {
      for (const entry of unreconciledEntries) {
        const selectedId = selectedAccountsByEntry[entry.id] || getAutoSelectedAccount(entry);
        if (!selectedId) continue; // skip if nothing selected for this entry

        // Determine debit/credit as in single reconcile
        const isReceivedForThisAccount = entry.entry_type === 'debit';
        const isSpentForThisAccount = entry.entry_type === 'credit';
        let debitAccountId, creditAccountId;
        if (isReceivedForThisAccount) {
          debitAccountId = account.id;
          creditAccountId = selectedId;
        } else if (isSpentForThisAccount) {
          debitAccountId = selectedId;
          creditAccountId = account.id;
        } else {
          continue;
        }

        const resp = await fetch('/api/internal/reconcile', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            entry_id: entry.id,
            debit_account_id: debitAccountId,
            credit_account_id: creditAccountId,
          }),
        });
        if (!resp.ok) {
          // fail gracefully but continue with others
          // eslint-disable-next-line no-console
          console.error('Auto reconcile failed for entry', entry.id);
        }
      }

      // Refresh after batch
      await fetchUnreconciledEntries();
      await fetchEntries();
      await fetchAccountDetails();
      setShowAutoReconcileDialog(false);
    } finally {
      setIsAutoReconciling(false);
    }
  };

  const openDeleteDialog = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteDialog(true);
  };

  if (error) {
    return (
      <PageLayout 
        currentUser={currentUser}
        companies={companies}
        currentCompany={currentCompany}
        containerProps={{ pt: 4, pb: 4 }}
      >
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading account details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
          <Button onClick={() => window.history.back()} sx={{ mt: 2 }}>
            <ArrowBackIcon sx={{ mr: 1 }} />
            Back
          </Button>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      currentUser={currentUser}
      companies={companies}
      currentCompany={currentCompany}
      containerProps={{ pt: 4, pb: 4 }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => window.history.back()}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <ArrowBackIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Back
          </Link>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={getTokenIcon(tokenName)}
              alt={`${tokenName} icon`}
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                mr: 2
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {selectedSource?.display_name} / {tokenName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account?.name} - ID: {account?.id}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Statement balance
            </Typography>
            {isCrypto ? (
              <>
                <FormatAmount amount={balances.statementBalance} currency="GBP" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <FormatCryptoAmount amount={balances.tokenBalance} symbol={tokenName} maximumFractionDigits={8} />
                </Typography>
              </>
            ) : (
              <FormatAmount amount={balances.statementBalance} currency={tokenName} />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Balance in ContaCrypto
            </Typography>
            {isCrypto ? (
              <>
                <FormatAmount amount={balances.contacryptoBalance} currency="GBP" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <FormatCryptoAmount amount={balances.contacryptoTokenBalance} symbol={tokenName} maximumFractionDigits={8} />
                </Typography>
              </>
            ) : (
              <FormatAmount amount={balances.contacryptoBalance} currency={tokenName} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('tab', newValue);
            window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
          }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          <Tab label="RECONCILE" value="reconcile" />
          <Tab label="WALLET STATEMENTS" value="statements" />
          <Tab label="RULES" value="rules" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 'statements' && (
        <Box>
          {/* Entries Table */}
          <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  
                  <TableCell>Spent</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>(£) Spent</TableCell>
                  <TableCell>(£) Received</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>Loading entries...</Typography>
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No entries found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => {
                    const statusProps = getStatusChipProps(entry.status);
                    const isExpanded = expandedEntry === entry.id;

                    // Determine which side (spent/received) corresponds to this account
                    const spentMeta = entry.metadata?.spent || {};
                    const receivedMeta = entry.metadata?.received || {};
                    
                    const isSpentForThisAccount = entry.entry_type === 'credit';
                    const isReceivedForThisAccount = entry.entry_type === 'debit';

                    const tokenAmount = isSpentForThisAccount && spentMeta.is_token ? Number(spentMeta.amount) : 
                                       isReceivedForThisAccount && receivedMeta.is_token ? Number(receivedMeta.amount) : null;
                    
                    const nativeAmount = isSpentForThisAccount && spentMeta.native_amount != null ? Number(spentMeta.native_amount) :
                                        isReceivedForThisAccount && receivedMeta.native_amount != null ? Number(receivedMeta.native_amount) : null;

                    // Debit = Received (asset increases), Credit = Spent (asset decreases)
                    const isDebit = isReceivedForThisAccount;

                    return (
                      <React.Fragment key={entry.id}>
                        <TableRow hover onClick={() => handleEntryClick(entry)}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.date)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box>
                              <Typography variant="body2">
                                {isDebit ? 'Received' : 'Spent'}
                              </Typography>
                              {(() => {
                                // Show profit for reconciled spent transactions
                                if (!isDebit && entry.status === 'reconciled' && entry.entry_type === 'credit') {
                                  const profitInfo = profitDataByEntry[entry.id];
                                  
                                  if (profitInfo) {
                                    return (
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          color: profitInfo.isPositive ? 'success.main' : 'error.main',
                                          fontSize: '0.7rem',
                                          display: 'block',
                                          mt: 0.5
                                        }}
                                      >
                                        {profitInfo.isPositive ? '+' : ''}{profitInfo.percentage.toFixed(1)}%
                                      </Typography>
                                    );
                                  }
                                }
                                return null;
                              })()}
                            </Box>
                          </TableCell>
                          <TableCell>{entry.description || '-'}</TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            {!isDebit && (tokenAmount || nativeAmount) ? (
                              isCrypto && tokenAmount != null ? (
                                <FormatCryptoAmount amount={tokenAmount} symbol={tokenName} />
                              ) : (
                                <FormatAmount amount={nativeAmount || entry.credit_amount} currency="GBP" />
                              )
                            ) : '-'}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            {isDebit && (tokenAmount || nativeAmount) ? (
                              isCrypto && tokenAmount != null ? (
                                <FormatCryptoAmount amount={tokenAmount} symbol={tokenName} />
                              ) : (
                                <FormatAmount amount={nativeAmount || entry.debit_amount} currency="GBP" />
                              )
                            ) : '-'}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            {!isDebit && entry.credit_amount > 0 ? (
                              <FormatAmount amount={entry.credit_amount} currency="GBP" />
                            ) : '-'}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 600 }}>
                            {isDebit && entry.debit_amount > 0 ? (
                              <FormatAmount amount={entry.debit_amount} currency="GBP" />
                            ) : '-'}
                          </TableCell>

                          <TableCell>
                            <Chip 
                              size="small"
                              label={entry.status}
                              sx={{
                                textTransform: 'capitalize',
                                bgcolor: statusProps.bgcolor,
                                color: statusProps.color,
                                fontWeight: 600
                              }}
                              icon={statusProps.icon}
                            />
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0 }}>
                              <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                {isLoadingReconciliation ? (
                                  <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography>Loading reconciliation details...</Typography>
                                  </Box>
                                ) : reconciliationData ? (
                                  <Box>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                      JOURNAL TRANSACTION
                                    </Typography>
                                    
                                    {/* Reconciliation Header */}
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {formatDate(reconciliationData.date)}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {reconciliationData.description}
                                          </Typography>
                                        </Box>
                                        {(() => {
                                          const profitInfo = calculateProfitPercentage(reconciliationData);
                                          const isSpentTransaction = entry.entry_type === 'credit';
                                          
                                          if (profitInfo && isSpentTransaction) {
                                            return (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AttachMoneyIcon 
                                                  sx={{ 
                                                    color: profitInfo.isPositive ? 'success.main' : 'error.main',
                                                    fontSize: 20
                                                  }} 
                                                />
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    fontWeight: 600,
                                                    color: profitInfo.isPositive ? 'success.main' : 'error.main'
                                                  }}
                                                >
                                                  {profitInfo.isPositive ? '+' : ''}{profitInfo.percentage.toFixed(2)}%
                                                </Typography>
                                              </Box>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </Box>
                                    </Box>

                                    {/* Profit Explanation */}
                                    {(() => {
                                      const profitInfo = calculateProfitPercentage(reconciliationData);
                                      const explanation = generateProfitExplanation(entry, reconciliationData, profitInfo);
                                      
                                      if (explanation) {
                                        return (
                                          <Box sx={{ mb: 2, p: 2, bgcolor: explanation.bgColor, borderRadius: 1, border: `1px solid ${explanation.color}` }}>
                                            <Typography 
                                              variant="subtitle2" 
                                              sx={{ 
                                                fontWeight: 600, 
                                                mb: 1,
                                                color: explanation.color
                                              }}
                                            >
                                              {explanation.title}
                                            </Typography>
                                            <Typography 
                                              variant="body2" 
                                              sx={{ 
                                                color: 'text.primary',
                                                lineHeight: 1.5
                                              }}
                                            >
                                              {explanation.content}
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                      return null;
                                    })()}

                                    {/* Reconciliation Entries Table */}
                                    <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Account</TableCell>
                                            <TableCell>Debit</TableCell>
                                            <TableCell>Credit</TableCell>
                                            <TableCell>(£) Debit</TableCell>
                                            <TableCell>(£) Credit</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {reconciliationData.amounts.map((amount, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{reconciliationData.description}</TableCell>
                                              <TableCell>
                                                {amount.account_code} - {amount.account_name}
                                              </TableCell>
                                              <TableCell>
                                                {amount.type === 'debit' ? (
                                                  <FormatAmount amount={amount.amount} currency={amount.currency} />
                                                ) : '-'}
                                              </TableCell>
                                              <TableCell>
                                                {amount.type === 'credit' ? (
                                                  <FormatAmount amount={amount.amount} currency={amount.currency} />
                                                ) : '-'}
                                              </TableCell>
                                              <TableCell>
                                                {amount.type === 'debit' ? (
                                                  <FormatAmount amount={amount.amount} currency="GBP" />
                                                ) : '-'}
                                              </TableCell>
                                              <TableCell>
                                                {amount.type === 'credit' ? (
                                                  <FormatAmount amount={amount.amount} currency="GBP" />
                                                ) : '-'}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                          {/* Empty rows to match the image */}
                                          {[...Array(Math.max(0, 4 - reconciliationData.amounts.length))].map((_, index) => (
                                            <TableRow key={`empty-${index}`}>
                                              <TableCell>-</TableCell>
                                              <TableCell>-</TableCell>
                                              <TableCell>-</TableCell>
                                              <TableCell>-</TableCell>
                                              <TableCell>-</TableCell>
                                              <TableCell>-</TableCell>
                                            </TableRow>
                                          ))}
                                          {/* Total row */}
                                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell colSpan={4} sx={{ fontWeight: 600 }}>
                                              Total
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              <FormatAmount 
                                                amount={reconciliationData.amounts
                                                  .filter(a => a.type === 'debit')
                                                  .reduce((sum, a) => sum + a.amount, 0)} 
                                                currency="GBP" 
                                              />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              <FormatAmount 
                                                amount={reconciliationData.amounts
                                                  .filter(a => a.type === 'credit')
                                                  .reduce((sum, a) => sum + a.amount, 0)} 
                                                currency="GBP" 
                                              />
                                            </TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>

                                    {/* Action buttons */}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button size="small" sx={{ textDecoration: 'underline', color: 'text.secondary' }}>
                                        Open In Xero
                                      </Button>
                                      <Button variant="contained" color="success" size="small">
                                        Sync with Xero
                                      </Button>
                                      <Button 
                                        variant="outlined" 
                                        size="small"
                                        onClick={() => openDeleteDialog(entry)}
                                      >
                                        Unreconcile
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="error">
                                    Failed to load reconciliation details
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 'reconcile' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Unreconciled Transactions
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={() => setShowAutoReconcileDialog(true)}
              startIcon={<CheckCircleIcon />}
              disabled={isLoadingUnreconciled || unreconciledEntries.length === 0}
              sx={{ textTransform: 'none', borderRadius: 999 }}
            >
              Auto reconcile selected
            </Button>
          </Box>

          {isLoadingUnreconciled ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading unreconciled entries...</Typography>
            </Box>
          ) : unreconciledEntries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No unreconciled entries found</Typography>
            </Box>
          ) : (
            unreconciledEntries.map((entry) => {
              const spentMeta = entry.metadata?.spent || {};
              const receivedMeta = entry.metadata?.received || {};
              const isSpentForThisAccount = entry.entry_type === 'credit';
              const isReceivedForThisAccount = entry.entry_type === 'debit';
              const tokenAmount = isSpentForThisAccount && spentMeta.is_token ? Number(spentMeta.amount) : 
                                  isReceivedForThisAccount && receivedMeta.is_token ? Number(receivedMeta.amount) : null;
              const nativeAmount = isSpentForThisAccount && spentMeta.native_amount != null ? Number(spentMeta.native_amount) :
                                  isReceivedForThisAccount && receivedMeta.native_amount != null ? Number(receivedMeta.native_amount) : null;

              const selectedAccountId = selectedAccountsByEntry[entry.id] || '';

              const labelId = `what-label-${entry.id}`;
              const selectId = `what-select-${entry.id}`;

              return (
                <Box key={entry.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 2, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  {/* Left: Transaction card (fixed width on md+) */}
                  <Box sx={{ width: { xs: '100%', md: 560 } }}>
                    <Card>
                      <CardContent sx={{ p: 2, minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {formatDate(entry.date)}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {entry.description || 'Transaction'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {entry.metadata?.payee || 'N/A'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Spent</Typography>
                            {isSpentForThisAccount && (tokenAmount || nativeAmount) ? (
                              isCrypto && tokenAmount != null ? (
                                <>
                                  {nativeAmount != null && (
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      <FormatAmount amount={nativeAmount} currency="GBP" />
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    <FormatCryptoAmount amount={tokenAmount} symbol={tokenName} />
                                  </Typography>
                                </>
                              ) : (
                                <FormatAmount amount={nativeAmount || entry.credit_amount} currency="GBP" />
                              )
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">Received</Typography>
                            {isReceivedForThisAccount && (tokenAmount || nativeAmount) ? (
                              isCrypto && tokenAmount != null ? (
                                <>
                                  {nativeAmount != null && (
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      <FormatAmount amount={nativeAmount} currency="GBP" />
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    <FormatCryptoAmount amount={tokenAmount} symbol={tokenName} />
                                  </Typography>
                                </>
                              ) : (
                                <FormatAmount amount={nativeAmount || entry.debit_amount} currency="GBP" />
                              )
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>

                  {/* Middle: OK button */}
                  <Box sx={{ width: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button 
                      variant="contained" 
                      color="success" 
                      onClick={() => handleReconcileOk(entry)}
                      disabled={!selectedAccountId}
                      sx={{ minWidth: 120 }}
                    >
                      OK
                    </Button>
                  </Box>

                  {/* Right: Account select (fixed width on md+) */}
                  <Box sx={{ width: { xs: '100%', md: 560 } }}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 2, minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          What
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Select account that you want to reconcile
                        </Typography>
                        <Autocomplete
                          options={companyAccounts}
                          loading={isLoadingAccounts}
                          value={companyAccounts.find(a => a.id === (selectedAccountsByEntry[entry.id] || '')) || null}
                          onChange={(e, val, reason) => handleAccountSelect(entry.id, val, reason)}
                          getOptionLabel={(option) => option ? `${option.code} - ${option.name}` : ''}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          renderInput={(params) => (
                            <TextField {...params} size="small" label="Choose the account" placeholder="Search account..." />
                          )}
                          fullWidth
                        />
                        {getAutoSelectedAccount(entry) && (
                          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                            ✓ Auto-selected based on rule
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      )}

      {activeTab === 'rules' && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Reconciliation Rules
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create rules to automatically select accounts for reconciliation based on transaction descriptions.
          </Typography>

          {isLoadingRules ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading rules...</Typography>
            </Box>
          ) : rulesError ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="error">{rulesError}</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Description Pattern</TableCell>
                    <TableCell>Target Account</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No rules found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Create your first rule to automate reconciliation
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {rule.description_pattern}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {rule.target_account_code} - {rule.target_account_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={rule.priority} 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditRule(rule)}
                            sx={{ mr: 1 }}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="small" 
                            color="error"
                            onClick={() => {
                              setRuleToDelete(rule);
                              setShowDeleteRuleDialog(true);
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => setShowAddRuleModal(true)}
            >
              Add New Rule
            </Button>
          </Box>
        </Box>
      )}

      {/* Add New Rule Modal */}
      <Dialog
        open={showAddRuleModal}
        onClose={() => setShowAddRuleModal(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="add-rule-dialog-title"
      >
        <DialogTitle id="add-rule-dialog-title">
          Add New Reconciliation Rule
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description Pattern"
              placeholder="e.g., SWAP ETH TO USDC"
              value={newRule.description_pattern}
              onChange={(e) => setNewRule({ ...newRule, description_pattern: e.target.value })}
              sx={{ mb: 3 }}
              helperText="Enter a pattern that matches transaction descriptions. Use % for wildcards."
            />
            
            <Autocomplete
              options={companyAccountsForRules}
              getOptionLabel={(option) => option ? `${option.code} - ${option.name}` : ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={companyAccountsForRules.find(acc => acc.id === newRule.target_account_id) || null}
              onChange={(e, val) => setNewRule({ ...newRule, target_account_id: val?.id || null })}
              loading={isLoadingCompanyAccountsForRules}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Target Account" 
                  placeholder="Search account..."
                  required
                />
              )}
              sx={{ mb: 3 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddRuleModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateRule} 
            variant="contained" 
            disabled={isCreatingRule || !newRule.description_pattern.trim() || !newRule.target_account_id}
          >
            {isCreatingRule ? 'Creating...' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rule Modal */}
      <Dialog
        open={showEditRuleModal}
        onClose={() => setShowEditRuleModal(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="edit-rule-dialog-title"
      >
        <DialogTitle id="edit-rule-dialog-title">
          Edit Reconciliation Rule
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description Pattern"
              placeholder="e.g., SWAP ETH TO USDC"
              value={editingRule.description_pattern}
              onChange={(e) => setEditingRule({ ...editingRule, description_pattern: e.target.value })}
              sx={{ mb: 3 }}
              helperText="Enter a pattern that matches transaction descriptions. Use % for wildcards."
            />
            
            <Autocomplete
              options={companyAccountsForRules}
              getOptionLabel={(option) => option ? `${option.code} - ${option.name}` : ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={companyAccountsForRules.find(acc => acc.id === editingRule.target_account_id) || null}
              onChange={(e, val) => setEditingRule({ ...editingRule, target_account_id: val?.id || null })}
              loading={isLoadingCompanyAccountsForRules}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Target Account" 
                  placeholder="Search account..."
                  required
                />
              )}
              sx={{ mb: 3 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditRuleModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateRule} 
            variant="contained" 
            disabled={isUpdatingRule || !editingRule.description_pattern.trim() || !editingRule.target_account_id}
          >
            {isUpdatingRule ? 'Updating...' : 'Update Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Delete Reconciliation */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Reconciliation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Do you want to delete the reconciliation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            No, Keep
          </Button>
          <Button onClick={handleDeleteReconciliation} color="error" variant="contained">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto Reconcile Confirmation */}
      <Dialog
        open={showAutoReconcileDialog}
        onClose={() => setShowAutoReconcileDialog(false)}
        aria-labelledby="auto-reconcile-dialog-title"
        aria-describedby="auto-reconcile-dialog-description"
      >
        <DialogTitle id="auto-reconcile-dialog-title">Auto reconcile</DialogTitle>
        <DialogContent>
          <DialogContentText id="auto-reconcile-dialog-description">
            This will reconcile all visible transactions that have a selected account
            (manually selected or auto-selected by rules). Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAutoReconcileDialog(false)}>Cancel</Button>
          <Button onClick={handleAutoReconcileAll} variant="contained" color="success" disabled={isAutoReconciling}>
            {isAutoReconciling ? 'Reconciling…' : 'Auto reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Delete Rule */}
      <Dialog
        open={showDeleteRuleDialog}
        onClose={() => setShowDeleteRuleDialog(false)}
        aria-labelledby="delete-rule-dialog-title"
        aria-describedby="delete-rule-dialog-description"
      >
        <DialogTitle id="delete-rule-dialog-title">
          Delete Rule
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-rule-dialog-description">
            Are you sure you want to delete the rule "{ruleToDelete?.description_pattern}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteRuleDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleDeleteRule(ruleToDelete?.id)} 
            color="error" 
            variant="contained"
          >
            Delete Rule
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

AccountDetailsPage.propTypes = {
  currentUser: PropTypes.object,
  companies: PropTypes.array,
  currentCompany: PropTypes.object,
  account: PropTypes.object,
  selectedSource: PropTypes.object,
  csrfToken: PropTypes.string
};

export default AccountDetailsPage; 
